import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { AlertTriangle, Download, Eye, Trash2, FileText, ArrowLeft } from 'lucide-react';
import IncidentReportSettings from '@/components/IncidentReportSettings';
import IncidentAIAnalysis from '@/components/IncidentAIAnalysis';
import PolicyViolations from '@/components/PolicyViolations';
import IncidentReportHistory from '@/components/IncidentReportHistory';
import { useTranslation } from 'react-i18next';
import { getLocalizedAI } from '@/utils/localizedAI';

const AdminIncidents = ({ user, setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const divisionIdFilter = searchParams.get('divisionId');
  const [incidentReports, setIncidentReports] = useState([]);
  const [showIncidentReportsDialog, setShowIncidentReportsDialog] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [incidentFilter, setIncidentFilter] = useState('all');
  const [updatingIncidentStatus, setUpdatingIncidentStatus] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [divisionName, setDivisionName] = useState(null);

  useEffect(() => {
    loadIncidentReports();
    if (divisionIdFilter) {
      loadDivisionName();
    }
  }, [divisionIdFilter]);

  const loadDivisionName = async () => {
    try {
      const response = await axiosInstance.get('/divisions');
      const division = response.data.find(d => d.id === divisionIdFilter);
      setDivisionName(division?.name || null);
    } catch (error) {
      console.error('Failed to load division:', error);
    }
  };

  const loadIncidentReports = async () => {
    setLoading(true);
    try {
      // Pass divisionId filter if present
      const url = divisionIdFilter 
        ? `/incident-reports?divisionId=${divisionIdFilter}` 
        : '/incident-reports';
      const response = await axiosInstance.get(url);
      setIncidentReports(response.data);
    } catch (error) {
      console.error('Failed to load incident reports:', error);
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const handleViewIncident = (report) => {
    setSelectedIncident(report);
    setShowIncidentReportsDialog(true);
  };

  const handleUpdateIncidentStatus = async (reportId, newStatus, reviewNotes = '') => {
    try {
      setUpdatingIncidentStatus(true);
      const response = await axiosInstance.put(`/incident-reports/${reportId}`, {
        status: newStatus,
        reviewNotes: reviewNotes || undefined
      });
      if (response.data && selectedIncident?.id === reportId) {
        setSelectedIncident(response.data);
      }
      toast.success(t('common.statusUpdated'));
      loadIncidentReports();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || t('common.saveFailed'));
    } finally {
      setUpdatingIncidentStatus(false);
    }
  };

  const handleDownloadIncidentPdf = async (reportId) => {
    try {
      const response = await axiosInstance.get(`/incident-reports/${reportId}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incident_report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('common.pdfDownloaded'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(t('common.pdfFailed'));
    }
  };

  const handleDeleteIncident = async (reportId, incidentType) => {
    if (!window.confirm(t('incidents.confirmDeleteIncident'))) {
      return;
    }
    try {
      await axiosInstance.delete(`/incident-reports/${reportId}`);
      toast.success(t('incidents.deleteIncidentSuccess'));
      setShowIncidentReportsDialog(false);
      loadIncidentReports();
    } catch (error) {
      console.error('Failed to delete incident report:', error);
      toast.error(error.response?.data?.detail || t('common.deleteFailed'));
    }
  };

  const handleExportAllIncidents = async () => {
    try {
      const response = await axiosInstance.post('/incident-reports/export-all', {}, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'all_incident_reports.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('incidents.allReportsExported'));
    } catch (error) {
      console.error('Failed to export reports:', error);
      toast.error(t('common.loadFailed'));
    }
  };

  const filteredIncidentReports = incidentFilter === 'all' 
    ? incidentReports.filter(r => 
        r.incidentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.reportedBy?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : incidentReports.filter(r => 
        r.status === incidentFilter &&
        (r.incidentType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         r.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
         r.reportedBy?.toLowerCase().includes(searchTerm.toLowerCase()))
      );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge variant="outline" className="bg-gray-100">{t('common.draft')}</Badge>;
      case 'submitted':
        return <Badge className="bg-blue-600">{t('common.submitted')}</Badge>;
      case 'under_review':
        return <Badge className="bg-yellow-600">{t('common.underReview')}</Badge>;
      case 'resolved':
        return <Badge className="bg-green-600">{t('common.resolved')}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/admin')}
          className="mb-6 gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('incidents.title')}</h1>
          <p className="text-gray-600 mt-1">{t('incidents.subtitle')}</p>
        </div>

        {/* Stats Overview - mobile responsive */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">{t('incidents.totalReports')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="text-2xl sm:text-3xl font-bold">{incidentReports.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">{t('common.submitted')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                {incidentReports.filter(r => r.status === 'submitted').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">{t('common.underReview')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="text-2xl sm:text-3xl font-bold text-yellow-600">
                {incidentReports.filter(r => r.status === 'under_review').length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2 sm:pb-3 px-3 sm:px-6">
              <CardTitle className="text-xs sm:text-sm font-medium text-gray-600">{t('common.resolved')}</CardTitle>
            </CardHeader>
            <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6">
              <div className="text-2xl sm:text-3xl font-bold text-green-600">
                {incidentReports.filter(r => r.status === 'resolved').length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Settings Card */}
        <div className="mb-6">
          <IncidentReportSettings />
        </div>

        {/* Incident Reports Table */}
        <Card>
          <CardHeader className="px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base sm:text-lg">{t('incidents.allIncidentReports')}</CardTitle>
                <CardDescription className="text-xs sm:text-sm">{t('incidents.clickToViewDetails')}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={loadIncidentReports} variant="outline" size="sm">
                  {t('common.refresh')}
                </Button>
                <Button onClick={handleExportAllIncidents} variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  {t('common.exportAll')}
                </Button>
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <Input
                placeholder={t('incidents.searchIncidents')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
              <Select value={incidentFilter} onValueChange={setIncidentFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t('common.filterByStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.allReports')}</SelectItem>
                  <SelectItem value="draft">{t('common.draft')}</SelectItem>
                  <SelectItem value="submitted">{t('common.submitted')}</SelectItem>
                  <SelectItem value="under_review">{t('common.underReview')}</SelectItem>
                  <SelectItem value="resolved">{t('common.resolved')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              </div>
            ) : filteredIncidentReports.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>{t('incidents.noIncidentReports')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredIncidentReports.map((report) => (
                  <div
                    key={report.id}
                    className="border rounded-lg p-3 sm:p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleViewIncident(report)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          {getStatusBadge(report.status)}
                          {report.aiAnalysis && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                              {t('common.aiAnalyzed')}
                            </Badge>
                          )}
                        </div>
                        <h4 className="font-semibold text-base sm:text-lg text-gray-900">{report.incidentType}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
                          {report.location} • {new Date(report.incidentDate).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          By: {report.reportedBy} on {new Date(report.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="self-start h-8 px-2 sm:px-3" onClick={(e) => {
                        e.stopPropagation();
                        handleViewIncident(report);
                      }}>
                        <Eye className="w-4 h-4 sm:mr-1" />
                        <span className="hidden sm:inline">View</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Incident Details Dialog */}
        <Dialog open={showIncidentReportsDialog} onOpenChange={setShowIncidentReportsDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {selectedIncident && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Incident Report: {selectedIncident.incidentType}
                  </DialogTitle>
                  <DialogDescription>
                    {t('common.reportedBy')} {selectedIncident.reportedBy} {t('common.on')} {new Date(selectedIncident.createdAt).toLocaleDateString()}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">{t('common.status')}</p>
                      <div className="mt-1">{getStatusBadge(selectedIncident.status)}</div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('incidents.dateAndTime')}</p>
                      <p className="font-medium">
                        {new Date(selectedIncident.incidentDate).toLocaleDateString()} at {selectedIncident.incidentTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('common.location')}</p>
                      <p className="font-medium">{selectedIncident.location}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{t('common.severity')}</p>
                      <p className="font-medium capitalize">{selectedIncident.severity}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{t('common.description')}</p>
                    <p className="text-gray-900 bg-gray-50 p-3 rounded">{selectedIncident.description}</p>
                  </div>

                  {/* Witness Statements */}
                  {selectedIncident.witnessStatements && selectedIncident.witnessStatements.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                      <p className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        {t('incidents.witnessStatements')} ({selectedIncident.witnessStatements.length})
                      </p>
                      <div className="space-y-3">
                        {selectedIncident.witnessStatements.map((statement, idx) => (
                          <div key={idx} className="bg-white p-3 rounded border border-blue-100">
                            <p className="text-sm font-medium text-gray-900 mb-1">
                              {statement.witnessName}
                            </p>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {statement.statement}
                            </p>
                            {statement.fileUrl && (
                              <a
                                href={statement.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline mt-2 inline-block"
                              >
                                {t('incidents.viewAttachedFile')}
                              </a>
                            )}
                            {statement.timestamp && (
                              <p className="text-xs text-gray-500 mt-1">
                                {t('incidents.recorded')}: {new Date(statement.timestamp).toLocaleString()}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Linked Documents */}
                  {(selectedIncident.linkedJSAs?.length > 0 || selectedIncident.linkedAudits?.length > 0) && (
                    <Card className="border-blue-200 bg-blue-50">
                      <CardHeader>
                        <CardTitle className="text-sm flex items-center gap-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          {t('incidents.linkedDocuments')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {selectedIncident.linkedJSAs?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-blue-900 mb-2">{t('incidents.jobSafetyAnalyses')} ({selectedIncident.linkedJSAs.length})</p>
                            <div className="space-y-2">
                              {selectedIncident.linkedJSAs.map((jsa) => (
                                <Button
                                  key={jsa.id}
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-start bg-white hover:bg-blue-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/admin/jsas?view=${jsa.id}`);
                                  }}
                                >
                                  <FileText className="w-3 h-3 mr-2 text-blue-600" />
                                  {jsa.task}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                        {selectedIncident.linkedAudits?.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-blue-900 mb-2">{t('incidents.safetyAudits')} ({selectedIncident.linkedAudits.length})</p>
                            <div className="space-y-2">
                              {selectedIncident.linkedAudits.map((audit) => (
                                <Button
                                  key={audit.id}
                                  variant="outline"
                                  size="sm"
                                  className="w-full justify-start bg-white hover:bg-blue-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/company/audits?view=${audit.id}`);
                                  }}
                                >
                                  <Eye className="w-3 h-3 mr-2 text-blue-600" />
                                  Audit on {audit.auditDate} - Score: {audit.complianceScore}%
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* AI Analysis */}
                  <IncidentAIAnalysis 
                    incidentReport={selectedIncident}
                    onAnalysisComplete={loadIncidentReports}
                    reportId={selectedIncident.id}
                    existingAnalysis={getLocalizedAI(selectedIncident, 'aiAnalysis')}
                  />

                  {/* Policy Violations */}
                  <PolicyViolations
                    recordKind="incident"
                    recordId={selectedIncident.id}
                    initialViolations={selectedIncident.policyViolations || []}
                    summary={selectedIncident.policyViolationsSummary || ''}
                    scannedAt={selectedIncident.policyViolationsScannedAt || ''}
                  />

                  {/* Review Notes */}
                  {selectedIncident.reviewNotes && (
                    <div className="bg-green-50 p-3 rounded">
                      <p className="text-sm font-medium text-green-900 mb-1">{t('incidents.reviewNotes')}:</p>
                      <p className="text-sm text-green-800">{selectedIncident.reviewNotes}</p>
                    </div>
                  )}

                  <IncidentReportHistory reportId={selectedIncident.id} />
                </div>

                <DialogFooter className="flex justify-between">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadIncidentPdf(selectedIncident.id)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      {t('incidents.downloadPdf')}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteIncident(selectedIncident.id, selectedIncident.incidentType)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      {t('common.delete')}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    {selectedIncident.status !== 'resolved' && (
                      <Button
                        onClick={() => handleUpdateIncidentStatus(selectedIncident.id, 'resolved', 'Marked as resolved')}
                        disabled={updatingIncidentStatus}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {t('common.markResolved')}
                      </Button>
                    )}
                    <Button variant="outline" onClick={() => setShowIncidentReportsDialog(false)}>
                      {t('common.close')}
                    </Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AdminIncidents;
